import type { PlaybackEventRecord } from "@led-signage/shared/management-api";

export type PlaybackEventTone = "ok" | "warning" | "error";

export interface PlaybackEventView {
  id: string;
  date: string;
  time: string;
  device: string;
  event: string;
  tone: PlaybackEventTone;
  media: string;
  position: string;
  release: string;
  detail: string | null;
}

const EVENT_LABELS: Readonly<
  Record<PlaybackEventRecord["eventType"], { label: string; tone: PlaybackEventTone }>
> = {
  play_started: { label: "再生開始", tone: "ok" },
  play_completed: { label: "再生完了", tone: "ok" },
  play_interrupted: { label: "再生中断", tone: "warning" },
  play_failed: { label: "再生失敗", tone: "error" },
  fallback_started: { label: "デフォルト動画へ切替", tone: "warning" },
  // ループ繰り返しの集約。回数は detail の completedCount にある。
  play_summary: { label: "繰り返し再生", tone: "ok" },
};

const DETAIL_LABELS: Readonly<Record<string, string>> = {
  network_unavailable: "ネットワークを利用できません",
  release_changed: "配信版が切り替わりました",
  MPV_EXITED: "再生プロセスが終了しました",
};

function knownDetail(record: PlaybackEventRecord): string | null {
  if (record.detail === null) return null;
  // 集約は回数が本体の情報なので、そのまま出す。
  const completed = record.detail.completedCount;
  if (typeof completed === "number") return `${String(completed)}回`;
  const value = record.detail.errorCode ?? record.detail.reason;
  if (typeof value !== "string") return null;
  return DETAIL_LABELS[value] ?? value;
}

export function positionLabel(positionMs: number | null): string {
  if (positionMs === null) return "—";
  const totalSeconds = Math.floor(positionMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

export function playbackEventView(
  record: PlaybackEventRecord,
  deviceName: (id: string) => string,
  mediaName: (id: string | null) => string,
): PlaybackEventView {
  const timestamp = new Date(record.occurredAt);
  const event = EVENT_LABELS[record.eventType];
  return {
    id: record.id,
    date: new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(timestamp),
    time: new Intl.DateTimeFormat("ja-JP", {
      timeZone: "Asia/Tokyo",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(timestamp),
    device: deviceName(record.deviceId),
    event: event.label,
    tone: event.tone,
    media: mediaName(record.mediaAssetId),
    position: positionLabel(record.positionMs),
    release: record.releaseId ?? "—",
    detail: knownDetail(record),
  };
}
